import React, { useMemo, useState } from "react";
import { Container, Row, Col, Table, Form } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ageData from "../../assets/data/ageverteil.json";
import { DATENSTAND } from "../../config/constants";
import { SEX_COLORS } from "../../config/colors";
// import the shared Excel utils
import { exportToExcel } from "../../utils/exportExcel";



// number formatters
const nfInt = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 0,
});

const nfRate1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// parse "27,5" -> 27.5 (number)
const parseRate = (v) => {
  if (v == null || v === "" || v === "NA") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isNaN(n) ? null : n;
};

// Format helper
function formatAgeLabel(ageOriginal) {
  if (!ageOriginal) return "";
  const s = String(ageOriginal).trim();

  const mRange = s.match(/(\d+)\s*bis\s*unter\s*(\d+)/i);
  if (mRange) {
    const lo = parseInt(mRange[1], 10);
    const hiRaw = parseInt(mRange[2], 10);
    const hi = hiRaw - 1;
    return `${lo}-${hi}`;
  }

  const mPlus = s.match(
    /^(\d+)\s*(?:jahre)?\s*(?:und\s*älter|oder\s*älter|\+)\s*$/i
  );
  if (mPlus) {
    const lo = parseInt(mPlus[1], 10);
    return `${lo}+`;
  }

  const mSingle = s.match(/(\d+)/);
  if (mSingle) {
    const lo = parseInt(mSingle[1], 10);
    return `${lo}+`;
  }

  return s;
}

// helper: which sex is allowed for an entity?
const getAllowedSexForEntit = (entit) => {
  const name = (entit || "").toLowerCase();

  // only men
  if (name.includes("prostata")) return "M";

  // only women (Gebärmutterkörper)
  if (name.includes("gebärmutterkörper")) {
    return "W";
  }

  // otherwise both sexes (if data exists)
  return null;
};

const Altersverteilung = () => {
  const [selectedYear, setSelectedYear] = useState("2023");
  const [selectedEntit, setSelectedEntit] = useState("Krebs gesamt ohne C44");

  const availableYears = useMemo(
    () =>
      [...new Set(ageData.map((d) => d.diayear))].sort(
        (a, b) => Number(a) - Number(b)
      ),
    []
  );

  const availableEntits = useMemo(() => {
    const seen = new Set();
    return ageData
      .filter((d) => {
        if (!seen.has(d.entit)) {
          seen.add(d.entit);
          return true;
        }
        return false;
      })
      .map((d) => ({
        entit: d.entit,
        label: d.entit,
      }))
      .sort((a, b) => {
        // Put this entity at the top
        if (a.label === "Krebs gesamt ohne C44") return -1;
        if (b.label === "Krebs gesamt ohne C44") return 1;

        return a.label.localeCompare(b.label, "de");
      });
  }, []);

  const filtered = useMemo(() => {
    const rows = ageData.filter(
      (d) => d.diayear === selectedYear && d.entit === selectedEntit
    );

    const groups = {};
    rows.forEach((row) => {
      const age = row.agegroup;
      if (!groups[age]) {
        groups[age] = { ageOriginal: age };
      }

      if (row.SEX === "M") {
        groups[age].menCases = row.n_wob_alterspez;
        groups[age].menRate = parseRate(row.Inz_wob_altersspez);
      } else if (row.SEX === "W") {
        groups[age].womenCases = row.n_wob_alterspez;
        groups[age].womenRate = parseRate(row.Inz_wob_altersspez);
      }
    });

    const sorted = Object.values(groups).sort((a, b) => {
      const getMin = (str) =>
        parseInt(String(str).match(/\d+/)?.[0] ?? 0, 10);
      return getMin(a.ageOriginal) - getMin(b.ageOriginal);
    });

    return sorted
      .map((group) => {
        const label = formatAgeLabel(group.ageOriginal);
        return { ...group, age: label };
      })
      .filter((group) => {
        const lo = parseInt(group.age.match(/\d+/)?.[0] ?? 0, 10);
        return lo >= 15;
      });
  }, [selectedYear, selectedEntit]);

  const xTicks = useMemo(() => {
    const labels = filtered.map((d) => d.age);
    return labels.filter((_, i) => i % 2 === 0);
  }, [filtered]);

  // which sex should actually be shown (for legend/graph/table)?
  const allowedSex = useMemo(
    () => getAllowedSexForEntit(selectedEntit),
    [selectedEntit]
  );

  const hasMenData = useMemo(
    () => filtered.some((r) => r.menCases != null || r.menRate != null),
    [filtered]
  );
  const hasWomenData = useMemo(
    () => filtered.some((r) => r.womenCases != null || r.womenRate != null),
    [filtered]
  );

  const showMen = allowedSex === "W" ? false : hasMenData;
  const showWomen = allowedSex === "M" ? false : hasWomenData;

  // ---------------------
  // EXCEL EXPORT BUTTON
  // ---------------------

  const handleExport = () => {
    const tableData = filtered.map((row) => ({
      Altersgruppe: row.age,
      "Fallzahlen Männer": row.menCases ?? "–",
      "Fallzahlen Frauen": row.womenCases ?? "–",
      "Rate Männer pro 100.000":
        row.menRate != null ? nfRate1.format(row.menRate) : "–",
      "Rate Frauen pro 100.000":
        row.womenRate != null ? nfRate1.format(row.womenRate) : "–",
    }));

    exportToExcel({
      filename: "altersverteilung",
      sheetName: "Altersverteilung",
      data: tableData,
      metadata: [
        ["Altersverteilung"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Altersverteilung</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Im linken Diagramm sehen sie die absolute Anzahl der Neuerkrankungen
          für 5-Jahres-Altersgruppen. Im rechten Diagramm ist berücksichtigt,
          dass es eine unterschiedlich große Anzahl an Einwohnerinnen und
          Einwohnern pro Altersgruppe gibt und die Neuerkrankungsfälle sind pro
          100.000 EW pro Altersgruppe dargestellt. Die Angaben beziehen sich
          jeweils auf das Alter zum Zeitpunkt der Diagnose.
        </p>
        <p>
          Detaillierte Zahlen finden Sie in der untenstehenden Tabelle. Sie
          haben die Möglichkeit sich die Auswertungen über das Auswahlmenü unter
          diesem Text für verschiedene Entitäten und verschiedene
          Diagnosejahre anzeigen zu lassen.
        </p>
      </div>

      {/* EXPORT BUTTON - same style & position as Zeitverlauf */}
      <button onClick={handleExport} className="top-btn mb-3">
        📥 Daten als Excel exportieren
      </button>

      {/* DROPDOWNS */}
      <div
        className="d-flex align-items-center gap-3 mb-3"
        style={{ maxWidth: 720 }}
      ></div>
      <Row className="mb-4">
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label>Krebs-Auswahl:</Form.Label>
            <Form.Select
              value={selectedEntit}
              onChange={(e) => setSelectedEntit(e.target.value)}
            >
              {availableEntits.map(({ entit, label }) => (
                <option key={entit} value={entit}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Jahr auswählen:</Form.Label>
            <Form.Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <h5>Krebsneuerkrankungen</h5>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={filtered}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" ticks={xTicks} />
              <YAxis />
              <Tooltip
                formatter={(value, name) => {
                  if (value == null || Number.isNaN(Number(value))) {
                    return ["–", name];
                  }
                  return [nfInt.format(value), name];
                }}
              />
              <Legend />
              {showMen && (
                <Bar dataKey="menCases" fill={SEX_COLORS.M} name="Männer" />
              )}
              {showWomen && (
                <Bar dataKey="womenCases" fill={SEX_COLORS.W} name="Frauen" />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Col>

        <Col md={6}>
          <h5>Krebsneuerkrankungsraten (Fälle pro 100.000)</h5>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={filtered}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" ticks={xTicks} />
              <YAxis />
              <Tooltip
                formatter={(value, name) => {
                  if (value == null || Number.isNaN(Number(value))) {
                    return ["–", name];
                  }
                  return [nfRate1.format(value), name];
                }}
              />
              <Legend />
              {showMen && (
                <Bar dataKey="menRate" fill={SEX_COLORS.M} name="Männer" />
              )}
              {showWomen && (
                <Bar dataKey="womenRate" fill={SEX_COLORS.W} name="Frauen" />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Col>
      </Row>

      {/* TABLE */}
      <h5 className="mt-5">Fallzahlen</h5>
      <Table
        responsive
        className="zeitverlauf-table text-center align-middle"
        style={{ maxWidth: "1000px" }}
      >
        <thead>
          <tr>
            <th rowSpan="2" className="text-start">
              Altersgruppe
            </th>
            <th colSpan={showMen && showWomen ? 2 : 1}>Fallzahlen</th>
            <th colSpan={showMen && showWomen ? 2 : 1}>
              Rate (pro 100.000)
            </th>
          </tr>
          <tr>
            {showMen && <th>Männer</th>}
            {showWomen && <th>Frauen</th>}
            {showMen && <th>Männer</th>}
            {showWomen && <th>Frauen</th>}
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => (
            <tr key={row.age}>
              <td className="text-start">{row.age}</td>
              {showMen && <td>{row.menCases ?? "–"}</td>}
              {showWomen && <td>{row.womenCases ?? "–"}</td>}
              {showMen && (
                <td>
                  {row.menRate != null ? nfRate1.format(row.menRate) : "–"}
                </td>
              )}
              {showWomen && (
                <td>
                  {row.womenRate != null ? nfRate1.format(row.womenRate) : "–"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default Altersverteilung;


